import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/Auth";
import { AppShell } from "@/components/AppShell";
import { Spinner } from "@/components/Common";
import { Landing } from "@/pages/Landing";
import { Login, Register, ForgotPassword, ResetPassword, VerifyEmail, AuthCallback } from "@/pages/AuthPages";
import Onboarding from "@/pages/Onboarding";
import { Scopri, FilteredFeed, ContentPage } from "@/pages/Scopri";
import { Daily, ContestPage } from "@/pages/Daily";
import Pubblica from "@/pages/Pubblica";
import Attivita from "@/pages/Attivita";
import Profilo from "@/pages/Profilo";
import Impostazioni from "@/pages/Impostazioni";
import Ricerca from "@/pages/Ricerca";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import { Messaggi, Chat } from "@/pages/Messaggi";
import Oggi, { MoodPath, Aiuto } from "@/pages/Oggi";
import Corpo from "@/pages/Corpo";
import Diario from "@/pages/Diario";
import Cresci from "@/pages/Cresci";
import Cresciamo from "@/pages/Cresciamo";
import Percorsi, { PathPage } from "@/pages/Percorsi";
import Ascolto from "@/pages/Ascolto";
import Stanze, { RoomPage } from "@/pages/Stanze";

function Protected({ children, admin }: { children: React.ReactNode; admin?: boolean }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-[100dvh] flex items-center justify-center"><Spinner /></div>;
  if (user === false) return <Navigate to={loc.pathname === "/" ? "/benvenuto" : "/accedi"} replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
}

function GuestOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user) return <Navigate to={user.onboarded ? "/" : "/onboarding"} replace />;
  return <>{children}</>;
}

function NeedsLogin({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/accedi" replace />;
  if (user.onboarded) return <Navigate to="/" replace />;
  return <>{children}</>;
}

const TOAST_OPTIONS = { className: "rounded-2xl border border-white/80 bg-[rgba(244,249,247,.94)] text-foreground backdrop-blur-xl" };

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const P = (el: React.ReactNode, admin = false) => <Protected admin={admin}>{el}</Protected>;
  return (
    <Routes>
      <Route path="/benvenuto" element={<GuestOnly><Landing /></GuestOnly>} />
      <Route path="/accedi" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/registrati" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/password-dimenticata" element={<ForgotPassword />} />
      <Route path="/reset" element={<ResetPassword />} />
      <Route path="/verifica" element={<VerifyEmail />} />
      <Route path="/onboarding" element={<NeedsLogin><Onboarding /></NeedsLogin>} />
      <Route path="/privacy" element={<Legal doc="privacy" />} />
      <Route path="/termini" element={<Legal doc="termini" />} />
      <Route path="/regolamento" element={<Legal doc="regolamento" />} />
      <Route path="/" element={P(<Oggi />)} />
      <Route path="/oggi/:mood" element={P(<MoodPath />)} />
      <Route path="/corpo" element={P(<Corpo />)} />
      <Route path="/diario" element={P(<Diario />)} />
      <Route path="/aiuto" element={P(<Aiuto />)} />
      <Route path="/cresci" element={P(<Cresci />)} />
      <Route path="/cresciamo" element={P(<Cresciamo />)} />
      <Route path="/percorsi" element={P(<Percorsi />)} />
      <Route path="/percorsi/:id" element={P(<PathPage />)} />
      <Route path="/ascolto" element={P(<Ascolto />)} />
      <Route path="/stanze" element={P(<Stanze />)} />
      <Route path="/stanze/:slug" element={P(<RoomPage />)} />
      <Route path="/feed" element={P(<Scopri />)} />
      <Route path="/daily" element={P(<Daily />)} />
      <Route path="/daily/:id" element={P(<ContestPage />)} />
      <Route path="/pubblica" element={P(<Pubblica />)} />
      <Route path="/attivita" element={P(<Attivita />)} />
      <Route path="/messaggi" element={P(<Messaggi />)} />
      <Route path="/messaggi/:id" element={P(<Chat />)} />
      <Route path="/profilo" element={P(<Profilo />)} />
      <Route path="/u/:username" element={P(<Profilo />)} />
      <Route path="/impostazioni" element={P(<Impostazioni />)} />
      <Route path="/cerca" element={P(<Ricerca />)} />
      <Route path="/categoria/:slug" element={P(<FilteredFeed kind="category" />)} />
      <Route path="/tag/:slug" element={P(<FilteredFeed kind="hashtag" />)} />
      <Route path="/c/:id" element={P(<ContentPage />)} />
      <Route path="/admin" element={P(<Admin />, true)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="light" position="top-center" offset={72} mobileOffset={{ top: 64 }} toastOptions={TOAST_OPTIONS} />
      </AuthProvider>
    </BrowserRouter>
  );
}
